// Suggestions from the files (Dean, Oct 2026): every one-off sub-timeline recorded on a file — by Fill with AI, by hand or from an insolvency
// record — is something the process did not offer on that arrow. The Head of Legal sees what, where and how often, and accepts or dismisses.
import { useCallback, useEffect, useState } from "react";
import { Check, Lightbulb, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { decideProcessSuggestion, listProcessSuggestions } from "@/lib/api";

const when = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "");

export function ProcessSuggestionsCard({ onGoTimeline }) {
  const [rows, setRows] = useState(null);
  const [head, setHead] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => {
    try { const r = await listProcessSuggestions(showAll ? "all" : "open"); setRows(r.suggestions || []); setHead(!!r.is_legal_head); } catch { setRows([]); }
  }, [showAll]);
  useEffect(() => { load(); }, [load]);
  const decide = async (s, status) => {
    setBusy(s.id);
    try {
      await decideProcessSuggestion(s.id, status);
      toast.success(status === "accepted" ? `Accepted — add ${s.name} to the arrow out of ${s.from_step_name} on ${s.main_name}, then publish` : "Dismissed");
      if (status === "accepted") onGoTimeline?.(s.main_timeline_id);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); } finally { setBusy(null); }
  };
  if (!rows) return null;
  const open = rows.filter((r) => r.status === "open");
  if (!rows.length && !showAll) return null;
  return (
    <div className="rounded-md border border-violet-200 bg-violet-50/40 px-3 py-2 space-y-1.5" data-testid="process-suggestions-card">
      <div className="flex items-center gap-2 flex-wrap">
        <Lightbulb className="w-3.5 h-3.5 text-violet-700" />
        <span className="text-xs font-semibold text-slate-900">Suggested additions from the files</span>
        <span className="text-[10px] text-slate-600">{open.length} open — one-offs the files recorded that the process does not offer on that arrow</span>
        <button type="button" className="ml-auto text-[10px] text-violet-800 hover:underline" onClick={() => setShowAll((v) => !v)} data-testid="process-suggestions-toggle">{showAll ? "open only" : "show decided"}</button>
      </div>
      {!rows.length && <div className="text-[11px] text-slate-500 italic">Nothing yet.</div>}
      <ul className="space-y-1">
        {rows.map((s) => (
          <li key={s.id} className={`rounded border px-2.5 py-1.5 text-[11px] flex items-start gap-2 flex-wrap ${s.status === "open" ? "bg-white border-violet-200" : "bg-slate-50 border-slate-200 text-slate-500"}`} data-testid={`process-suggestion-${s.id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-slate-900"><span className="font-semibold">{s.name}</span> on the arrow out of <span className="font-medium">{s.from_step_name}</span> · {s.main_name}{s.kind === "new_sub_timeline" && <span className="ml-1.5 text-[9px] px-1 rounded bg-slate-800 text-white">new</span>}</div>
              <div className="text-[10px] text-slate-500">
                {s.count} time{s.count === 1 ? "" : "s"} · last {when(s.last_at)} · {(s.occurrences || []).slice(-3).map((o) => `${o.customer_name || o.case_id}${o.party ? ` (${o.party})` : ""} — ${o.origin === "ai_fill" ? "Fill with AI" : o.origin === "insolvency" ? "insolvency record" : "by hand"}`).join(" · ")}
                {s.status !== "open" && ` · ${s.status} by ${s.decided_by || "—"} ${when(s.decided_at)}`}
              </div>
            </div>
            {s.status === "open" && head && (
              <span className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="outline" className="h-6 text-[10px] border-emerald-300 text-emerald-800 hover:bg-emerald-50" disabled={busy === s.id} onClick={() => decide(s, "accepted")} data-testid={`process-suggestion-accept-${s.id}`}>{busy === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}Accept</Button>
                <Button size="sm" variant="ghost" className="h-6 text-[10px] text-slate-600" disabled={busy === s.id} onClick={() => decide(s, "dismissed")} data-testid={`process-suggestion-dismiss-${s.id}`}><X className="w-3 h-3 mr-1" />Dismiss</Button>
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
